import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Header from "./components/Header";
import Hero from "./components/Hero";
import HowItWorks from "./components/HowItWorks";
import BeforeAfter from "./components/BeforeAfter";
import Pricing from "./components/Pricing";
import FAQ from "./components/FAQ";
import CTABanner from "./components/CTABanner";
import Footer from "./components/Footer";
import ChatDemo from "./components/ChatDemo";
import TrialOnboarding, { INVITE_KEY } from "./components/TrialOnboarding";
import DynamicTestLab from "./components/DynamicTestLab";
import { forgetAdmin } from "./data/labSync";
import EmailConfirmation from "./components/EmailConfirmation";
import LegalPage from "./components/LegalPage";
import { PRIVACY, TERMS } from "./data/legalContent";
import SmsConsentPage from "./components/SmsConsentPage";
import FamilySettings from "./components/FamilySettings";
import PasswordReset from "./components/PasswordReset";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import AdminConsole from "./components/admin/AdminConsole";
import {
  SESSION_EXPIRED_EVENT,
  apiFetch,
  clearSession,
  loadSession,
  saveSession,
  sessionFromAccount,
} from "./data/session";

const Home = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [scheduleTick, setScheduleTick] = useState(0);
  const [schedule, setSchedule] = useState({
    timezone: "America/Toronto",
    slotIds: [],
    exceptions: [],
    dailyPlan: "18:00",
    weeklyPlanDay: "jeu",
    weeklyPlanTime: "16:00",
    appointments: [],
  });
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginMode, setLoginMode] = useState("login");
  const [loginNotice, setLoginNotice] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [session, setSession] = useState(() => loadSession());
  const [immersive, setImmersive] = useState(false);
  const openDemo = () => setDemoOpen(true);
  const closeDemo = () => setDemoOpen(false);
  const openTrial = () => setTrialOpen(true);
  const closeTrial = () => setTrialOpen(false);

  useEffect(() => {
    const expired = () => {
      setSession(null);
      setImmersive(false);
      setLoginNotice("Votre session a expiré. Reconnectez-vous.");
      setLoginMode("login");
      setLoginOpen(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
  }, []);

  useEffect(() => {
    const invite = new URLSearchParams(window.location.search).get("invite");
    if (!invite) return;
    sessionStorage.setItem(INVITE_KEY, invite);
    window.history.replaceState(null, "", window.location.pathname);
    setTrialOpen(true);
  }, []);

  useEffect(() => {
    // An old session (confirmation token) or a revoked one ends here with a 401.
    if (!session?.token) return;
    apiFetch("/api/auth/me", session.token)
      .then((data) => {
        const fresh = { forfait: Number(data.forfait) || 1, onboarded: Boolean(data.onboarded) };
        if (fresh.forfait !== session.forfait || fresh.onboarded !== Boolean(session.onboarded)) {
          const next = { ...session, ...fresh };
          if (!session.studentId && data.studentId) Object.assign(next, { studentId: data.studentId, studentName: data.studentName || session.studentName });
          saveSession(next);
          setSession(next);
          return;
        }
        // A child invited from Settings or the chat: pick it up once, at load.
        if (!session.studentId && data.studentId) {
          const next = { ...session, studentId: data.studentId, studentName: data.studentName || session.studentName };
          saveSession(next);
          setSession(next);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token]);

  useEffect(() => {
    if (!session?.token) {
      setSchedule({
        timezone: "America/Toronto",
        slotIds: [],
        exceptions: [],
        dailyPlan: "18:00",
        weeklyPlanDay: "jeu",
        weeklyPlanTime: "16:00",
        appointments: [],
      });
      return undefined;
    }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    let cancelled = false;
    fetch(`${base}/api/family/schedule`, { headers: { "X-Account-Token": session.token } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setSchedule({
            timezone: data.timezone || "America/Toronto",
            slotIds: data.slotIds || [],
            exceptions: data.exceptions || [],
            dailyPlan: data.dailyPlan || "18:00",
            weeklyPlanDay: data.weeklyPlanDay || "jeu",
            weeklyPlanTime: data.weeklyPlanTime || "16:00",
            appointments: data.appointments || [],
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session, scheduleTick]);

  const saveSnapshot = useCallback(
    (payload) => {
      if (!session?.token || !session.studentId) return;
      const base = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${base}/api/study/${session.studentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Account-Token": session.token },
        body: JSON.stringify(payload),
      }).catch(() => {});
    },
    [session]
  );

  const saveTutorName = (name) => {
    if (!session?.token || session.role !== "student") return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/me/tutor-name`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Account-Token": session.token },
      body: JSON.stringify({ tutorName: name }),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data?.tutorName) return;
        const next = { ...session, tutorName: data.tutorName };
        saveSession(next);
        setSession(next);
      })
      .catch(() => {});
  };

  const saveAvailability = (slotIds) => {
    if (!session?.token) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const next = { ...schedule, slotIds };
    setSchedule(next);
    fetch(`${base}/api/family/schedule`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Account-Token": session.token },
      body: JSON.stringify(next),
    }).catch(() => {});
  };

  const logout = () => {
    if (session?.token) apiFetch("/api/auth/logout", session.token, { method: "POST" }).catch(() => {});
    if (session?.role === "admin" || session?.integrator) forgetAdmin();
    clearSession();
    setSession(null);
    setImmersive(false);
  };

  const startSession = (data, fallbackEmail = "") => {
    const next = sessionFromAccount(data, data.token, fallbackEmail);
    saveSession(next);
    setSession(next);
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    setLoginNotice("");
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      if (loginMode === "forgot") {
        const response = await fetch(`${base}/api/auth/password/forgot`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: loginEmail, appUrl: window.location.origin }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          setLoginError(data.detail || "Envoi impossible.");
          return;
        }
        setLoginNotice(
          data.resetPath
            ? `Mode local : aucun courriel envoyé. Lien : ${data.resetPath}`
            : "Si un compte existe pour ce courriel, un lien vient d’être envoyé. Il reste valide une heure."
        );
        return;
      }
      const response = await fetch(`${base}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setLoginError(data.detail || "Connexion impossible.");
        return;
      }
      startSession(data, loginEmail);
      setLoginOpen(false);
      setLoginEmail("");
      setLoginPassword("");
    } catch {
      setLoginError("Le serveur ne répond pas.");
    } finally {
      setLoginBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen max-w-[100vw] flex-col overflow-x-hidden bg-white text-[#122118]">
      {!immersive && (
      <Header
        onTryDemo={openDemo}
        onStartTrial={openTrial}
        onLogin={() => {
          setLoginError("");
          setLoginNotice("");
          setLoginMode("login");
          setLoginOpen(true);
        }}
        session={session}
        onLogout={logout}
        onOpenSimulator={() => setLabOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenAdmin={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        onChangePassword={() => setPasswordOpen(true)}
      />
      )}
      {session?.role === "admin" ? (
        <main className="flex-1 bg-[#F7FAF8]">
          <AdminConsole session={session} />
        </main>
      ) : session ? (
        <main className={immersive ? "flex h-[100dvh] max-w-[100vw] flex-1 overflow-hidden bg-[#EFEAE2]" : "flex max-w-[100vw] flex-1 items-start justify-center overflow-x-hidden overflow-y-auto px-2 pb-2 pt-20 sm:px-4 sm:pb-8"}>
          <div data-testid="home-live-phone">
            <DynamicTestLab
              key={`${session.email}-${session.role}`}
              open
              variant="app"
              initialRole={session.role === "student" ? "eleve" : "parent"}
              studentName={session.studentName}
              parentName={session.parentName}
              availabilitySeed={schedule.slotIds}
              scheduleNote={(schedule.exceptions || []).map((item) => `${item.date}${item.note ? ` · ${item.note}` : ""}`).join(", ")}
              onAvailabilityCommit={saveAvailability}
              onSnapshot={saveSnapshot}
              onImmersive={setImmersive}
              tutorName={session.role === "student" ? session.tutorName || "" : ""}
              onTutorName={saveTutorName}
              accountToken={session.token}
              onLogout={logout}
              onOpenSettings={() => setSettingsOpen(true)}
              accountForfait={session.forfait || 1}
              accountOnboarded={Boolean(session.onboarded)}
              accountId={session.id}
              studentId={session.studentId || ""}
              hasStudent={session.role !== "parent" || Boolean(session.studentId)}
              onStudentInvited={(member) => {
                // Saved for the next visit; the open conversation keeps its thread.
                if (!session.studentId && member?.id) {
                  saveSession({ ...session, studentId: member.id, studentName: member.name || session.studentName });
                }
              }}
            />
          </div>
        </main>
      ) : (
        <main>
          <Hero onTryDemo={openDemo} onStartTrial={openTrial} />
          <HowItWorks />
          <BeforeAfter />
          <Pricing onStartTrial={openTrial} />
          <FAQ />
          <CTABanner onTryDemo={openDemo} onStartTrial={openTrial} />
        </main>
      )}
      {!immersive && <Footer />}

      <ChatDemo open={demoOpen} onClose={closeDemo} />
      <TrialOnboarding
        open={trialOpen}
        onClose={closeTrial}
      />
      <DynamicTestLab
        open={labOpen}
        onClose={() => setLabOpen(false)}
        studentName={session?.studentName || "Émile"}
        parentName={session?.parentName || "Sophie"}
        availabilitySeed={schedule.slotIds}
        scheduleNote={(schedule.exceptions || []).map((item) => `${item.date}${item.note ? ` · ${item.note}` : ""}`).join(", ")}
        onAvailabilityCommit={saveAvailability}
        onSnapshot={saveSnapshot}
        tutorName={session?.role === "student" ? session.tutorName || "" : ""}
        onTutorName={saveTutorName}
      />
      {passwordOpen && session && (
        <ChangePasswordDialog token={session.token} onClose={() => setPasswordOpen(false)} />
      )}
      {settingsOpen && session && (
        <FamilySettings
          session={session}
          onClose={() => {
            setSettingsOpen(false);
            setScheduleTick((value) => value + 1);
          }}
          onStudentFocus={(name, studentId) => {
            const next = {
              ...session,
              studentName: name || session.studentName,
              studentId: studentId || session.studentId,
            };
            saveSession(next);
            setSession(next);
          }}
        />
      )}
      {loginOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0E1A14]/55 p-4">
          <form
            onSubmit={submitLogin}
            data-testid="login-dialog"
            className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
          >
            <h2 className="font-display text-2xl font-bold text-[#122118]">
              {loginMode === "forgot" ? "Mot de passe oublié" : "Se connecter"}
            </h2>
            <p className="mt-2 text-sm text-[#4a5f53]">
              {loginMode === "forgot"
                ? "Entrez le courriel du compte. Vous recevrez un lien pour choisir un mot de passe."
                : "Entrez le courriel et le mot de passe du compte."}
            </p>
            <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-[#189346]" htmlFor="login-email">
              Courriel
            </label>
            <input
              id="login-email"
              data-testid="login-email"
              type="email"
              required
              value={loginEmail}
              onChange={(event) => setLoginEmail(event.target.value)}
              className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#25D366]"
            />
            {loginMode === "login" && (
              <>
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-[#189346]" htmlFor="login-password">
                  Mot de passe
                </label>
                <input
                  id="login-password"
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={loginPassword}
                  onChange={(event) => setLoginPassword(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#25D366]"
                />
              </>
            )}
            <button
              type="button"
              data-testid="login-toggle-forgot"
              onClick={() => {
                setLoginError("");
                setLoginNotice("");
                setLoginMode(loginMode === "forgot" ? "login" : "forgot");
              }}
              className="mt-3 text-sm font-semibold text-[#128a3e] underline-offset-2 hover:underline"
            >
              {loginMode === "forgot" ? "Retour à la connexion" : "Mot de passe oublié ou jamais choisi ?"}
            </button>
            {loginNotice && (
              <p className="mt-3 break-all text-sm text-[#2d5a3d]" data-testid="login-notice">
                {loginNotice}
              </p>
            )}
            {loginError && (
              <p className="mt-3 text-sm text-[#9b3b3b]" data-testid="login-error">
                {loginError}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setLoginOpen(false)}
                className="rounded-full px-4 py-2 text-sm font-semibold text-[#122118]"
              >
                Annuler
              </button>
              <button
                type="submit"
                data-testid="login-submit"
                disabled={loginBusy}
                className="rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}
              >
                {loginMode === "forgot" ? "Recevoir le lien" : "Se connecter"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/confirmer/:token" element={<EmailConfirmation />} />
          <Route path="/reinitialiser/:token" element={<PasswordReset />} />
          <Route path="/confidentialite" element={<LegalPage content={PRIVACY} />} />
          <Route path="/conditions-utilisation" element={<LegalPage content={TERMS} />} />
          <Route path="/sms-consentement" element={<SmsConsentPage />} />
          <Route path="/conditions" element={<Navigate to="/conditions-utilisation" replace />} />
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
